import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/notification/NotificationService';
import { NotificationsQueryFilters } from '../types/notification';
import { DashboardQueryKey } from '../constants/dashboardQueryKeys';

export const notificationKeys = {
  base: () => [DashboardQueryKey.NOTIFICATIONS] as const,
  all: (page: number, pageSize: number, filters?: NotificationsQueryFilters) =>
    [DashboardQueryKey.NOTIFICATIONS, page, pageSize, filters ?? null] as const,
  unreadCount: () => [DashboardQueryKey.NOTIFICATIONS_UNREAD_COUNT] as const,
};

const UNREAD_REFETCH_INTERVAL_MS = 30_000;

function invalidateNotificationCaches(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: notificationKeys.base() });
  queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
}

export function useNotificationsQuery(
  page = 1,
  pageSize = 20,
  filters?: NotificationsQueryFilters,
) {
  return useQuery({
    queryKey: notificationKeys.all(page, pageSize, filters),
    queryFn: () => notificationService.getNotifications(page, pageSize, filters),
  });
}

export function useUnreadCountQuery() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: UNREAD_REFETCH_INTERVAL_MS,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => invalidateNotificationCaches(queryClient),
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => invalidateNotificationCaches(queryClient),
  });
}

export function useBroadcastNotification() {
  return useMutation({
    mutationFn: ({ title, message }: { title: string; message: string }) =>
      notificationService.broadcast(title, message),
  });
}
